import { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  faCheckCircle,
  faQuestionCircle,
} from '@fortawesome/free-regular-svg-icons'
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'

import companiesHouseLogo from '@/assets/img/monitoring/companies-house.png'
import fcaLogo from '@/assets/img/monitoring/fca.png'
import fosLogo from '@/assets/img/monitoring/fos.png'
import fscsLogo from '@/assets/img/monitoring/fscs.png'
import CollapsibleContent from '@/components/misc/CollapsibleContent'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { DATE_FORMATS, FCA_MONITORING_CODE } from '@/lib/constants'
import { formatDate } from '@/lib/helpers/dateHelpers'
import { cn } from '@/lib/utils'
import { FcaMonitoring } from '@/types/misc'
import { DirectoryFields } from '@/types/responses/directory'

interface Props {
  fca: DirectoryFields
}

interface AccordionItem {
  headerText: string
  logo: string
  monitoring: FcaMonitoring | undefined
  additionalText?: string | null
}

const MonitoringTab: React.FC<Props> = ({ fca }) => {
  const fscs1 = findMonitoringByCode(FCA_MONITORING_CODE.FSCS1)
  const fscs2 = findMonitoringByCode(FCA_MONITORING_CODE.FSCS2)

  function findMonitoringByCode(code: string): FcaMonitoring | undefined {
    return fca.monitoring.find((item) => item.code === code)
  }

  const COMBINED_FSCS_CODE = 'FSCS'

  // We need to combine the two FSCS items into one
  const fscsItem = (): FcaMonitoring | undefined => {
    if (!fscs1 && !fscs2) {
      return undefined
    }

    return {
      flag: fscs1?.flag !== false && fscs2?.flag !== false,
      message: (fscs1 ? fscs1.message + '<br /><br />' : '') + fscs2?.message,
      lastUpdated: fscs1 ? fscs1.lastUpdated : null,
      code: COMBINED_FSCS_CODE,
    }
  }

  const accordionItems: AccordionItem[] = [
    {
      headerText: 'Financial Conduct Authority',
      logo: fcaLogo,
      monitoring: findMonitoringByCode(FCA_MONITORING_CODE.FCA),
      additionalText: fca.fca?.frnTradingNameId
        ? `Note: This data relates to ${fca.fca?.name} more generally and not ${fca.name} specifically`
        : null,
    },
    {
      headerText: 'Financial Ombudsman Service',
      logo: fosLogo,
      monitoring: findMonitoringByCode(FCA_MONITORING_CODE.FOS),
    },
    {
      headerText: 'Financial Services Compensation Scheme',
      logo: fscsLogo,
      monitoring: fscsItem(),
      additionalText: fca.fca?.frnTradingNameId
        ? `Note: This data relates to ${fca.fca?.name} more generally and not ${fca.name} specifically`
        : null,
    },
    {
      headerText: 'Companies House',
      logo: companiesHouseLogo,
      monitoring: findMonitoringByCode(FCA_MONITORING_CODE.CompaniesHouse),
      additionalText: fca.fca?.frnTradingNameId
        ? `Note: This data relates to ${fca.fca?.name} more generally and not ${fca.name} specifically`
        : null,
    },
  ]

  function renderAccordionButton(monitoring: FcaMonitoring) {
    const { icon, className } = getAccordionButtonStyles(monitoring)

    return (
      <>
        <div className="inline-block">
          <FontAwesomeIcon
            icon={icon}
            className={cn('mr-4', className)}
            size="2x"
          />
        </div>

        <div
          className={cn('inline-block', {
            hidden: !monitoring.message,
          })}
        ></div>
      </>
    )
  }

  function getAccordionButtonStyles(monitoring: FcaMonitoring): {
    icon: IconProp
    className: string
  } {
    const hasRedFlag = fca.monitoringRedFlags.includes(monitoring.code)

    if (hasRedFlag) {
      return { icon: faTimesCircle, className: 'text-destructive' }
    }

    return monitoring.flag
      ? { icon: faCheckCircle, className: 'text-success' }
      : { icon: faQuestionCircle, className: 'text-warning' }
  }

  return (
    <Accordion type="single" collapsible className="rounded-lg border">
      {accordionItems.map((accordionItem) => {
        const {
          headerText,
          logo,
          monitoring,
          additionalText = null,
        } = accordionItem

        const isDataUnavailable = monitoring && monitoring.flag === null

        const backgroundColour =
          monitoring && monitoring.flag === false
            ? 'accordion-header--warning-light'
            : 'bg-white'

        return (
          <AccordionItem
            key={headerText}
            value={headerText}
            className="border-b px-4 font-light last:border-b-0"
            disabled={isDataUnavailable}
          >
            <AccordionTrigger
              className={cn('flex items-center', backgroundColour, {
                'opacity-50': isDataUnavailable,
              })}
            >
              <div className="flex grow items-center gap-4 font-light">
                <img
                  src={logo}
                  className="mr-12"
                  alt="FCA"
                  style={{ width: '80px' }}
                />
                {headerText}
              </div>

              {monitoring && (
                <div>
                  {isDataUnavailable ? (
                    <div className="p-4">No data available</div>
                  ) : (
                    <>{renderAccordionButton(monitoring)}</>
                  )}
                </div>
              )}
            </AccordionTrigger>

            {monitoring && monitoring.message && monitoring.message.length && (
              <AccordionContent className={backgroundColour}>
                <CollapsibleContent
                  allowHtml
                  content={
                    (additionalText
                      ? '<b>' + additionalText + '</b><br/><br/>'
                      : '') +
                    (monitoring.lastUpdated
                      ? 'Last updated: ' +
                        formatDate(
                          monitoring.lastUpdated,
                          DATE_FORMATS.MONTH_DAY_YEAR,
                        ) +
                        '<br/><br/>'
                      : '') +
                    monitoring.message
                  }
                />
              </AccordionContent>
            )}
          </AccordionItem>
        )
      })}
    </Accordion>
  )
}

export default MonitoringTab
